import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Flame, ChevronRight, Compass, Blocks, Gauge, ShieldCheck, Crown } from 'lucide-react';
import api from '../services/api';

export const LEVEL_ICONS = [Compass, Blocks, Gauge, ShieldCheck, Crown];

/**
 * WellnessJourneyCard — compact Dashboard surface for the gamification layer.
 * Taps through to the full /journey page. Silent until data is ready so it
 * never flashes an empty shell.
 */
const WellnessJourneyCard = () => {
  const [j, setJ] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.getJourney()
      .then((r) => { if (mounted) setJ(r.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading || !j) return null;
  const Icon = LEVEL_ICONS[j.level.index] || Compass;

  return (
    <Link
      to="/journey"
      className="hpme-card p-5 block group hover:border-primary/50 transition-colors overflow-hidden"
      data-testid="dashboard-journey-card"
    >
      <div className="hpme-card-corner" />
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] uppercase tracking-[0.2em] text-primary font-secondary font-bold">Wellness Journey</span>
        <span className="flex items-center gap-1 text-xs text-zinc-500 group-hover:text-primary transition-colors">
          Open <ChevronRight className="w-4 h-4" />
        </span>
      </div>
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-sm bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
          <Icon className="w-7 h-7 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="font-secondary font-bold text-lg text-white leading-tight truncate">
              Level {j.level.index + 1} · {j.level.name}
            </p>
            {j.streak_days > 0 && (
              <span className="flex items-center gap-1 text-orange-400 text-sm font-bold flex-shrink-0" data-testid="journey-card-streak">
                <Flame className="w-4 h-4" />{j.streak_days}d
              </span>
            )}
          </div>
          {!j.level.is_max ? (
            <>
              <div className="mt-2 h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${j.level.pct}%` }} />
              </div>
              <p className="text-[11px] text-zinc-500 mt-1.5">
                {Math.max(0, (j.level.next_xp || 0) - j.total_xp)} XP to {j.level.next_name}
              </p>
            </>
          ) : (
            <p className="text-[11px] text-primary mt-2">Max level reached — {j.total_xp} XP. Keep the streak alive.</p>
          )}
        </div>
      </div>
    </Link>
  );
};

export default WellnessJourneyCard;
